import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { InfoCircleFilled, PlusCircleOutlined } from '@ant-design/icons'
import { Avatar, Button, Divider, Form, Input, Modal, notification, Select, Space, Tooltip, Typography } from 'antd'
import debounce from 'lodash.debounce'

import { Team } from '@pollination-solutions/pollination-sdk'

interface CreateTeamPayload {
  name: string
  description?: string
}

interface SearchTeamsProps {
  searchTeams: (search?: string) => Promise<Team[]>
  selectTeam: (teams: Team) => void
  createTeam?: (createTeam: CreateTeamPayload) => Promise<Team | undefined>
}

export const SearchTeams: React.FunctionComponent<SearchTeamsProps> = ({ searchTeams, selectTeam, createTeam }) => {
  const [search, setSearch] = useState<string>('')
  const [teams, setTeams] = useState<Team[]>([])
  const [selectedTeam, setSelectedTeam] = useState<Team | undefined>()

  const [createTeamModalVisible, setCreateTeamModalVisible] = useState<boolean>(false)
  const [createTeamLoading, setCreateTeamLoading] = useState<boolean>(false)

  const debounceSearch = useMemo(() => debounce((search: string) => setSearch(search), 200), [])

  const createAndAddTeam = useCallback(async (payload: CreateTeamPayload) => {
    if (!createTeam) return

    setCreateTeamLoading(true)
    createTeam(payload).then((team) => {
      setSelectedTeam(team)
    }).finally(() => {
      setCreateTeamModalVisible(false)
      setCreateTeamLoading(false)
    })
  }, [createTeam, setCreateTeamModalVisible, setCreateTeamLoading, setSelectedTeam])

  useEffect(() => {
    searchTeams(search).then((teams) => setTeams(teams))
      .catch(error => {
        notification.error({
          message: error.data.details
        })
      })
  }, [search, searchTeams, setTeams])

  useEffect(() => {
    if (selectedTeam) {
      selectTeam(selectedTeam)
    }
  }, [selectedTeam, selectTeam])

  return (
    <>
      <Select
        style={{ minWidth: 240 }}
        showSearch
        showArrow
        placeholder='Search Teams'
        onSearch={debounceSearch}
        value={selectedTeam?.slug}
        onSelect={(value) => {
          const team = teams.find(team => team.slug === value)
          if (team) {
            setSelectedTeam(team)
          }
        }}
      >
        <Select.Option key='create-new-team' value='create-new-team'>

          <Button type='link' value='create-new-team' onClick={() => setCreateTeamModalVisible(true)}>
            <PlusCircleOutlined style={{ color: '#1890ff' }} /> New Team
          </Button>
        </Select.Option>
        {
          teams.map(team => (
            <Select.Option key={team.name} value={team.slug}>
            <Space size='small' align='center'>
              <Typography.Text>{team.name}</Typography.Text>
              <Tooltip title={
                <Space direction='vertical'>
                  <strong>{team.slug}</strong>
                  <Divider style={{
                    margin: 0
                  }}/>
                  {team.description}
                </Space>}
              >
                <InfoCircleFilled 
                style={{
                  color: '#1890ff',
                }} />
              </Tooltip>
            </Space>
            </Select.Option>
          ))
        }
      </Select>
      <Modal
        title='Create New Team'
        open={createTeamModalVisible}
        onCancel={() => setCreateTeamModalVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          name="mainForm"
          onFinish={createAndAddTeam}
          size='middle'
          initialValues={{
            name: search
          }}
        >
          <Form.Item
            name="name" label="Name" rules={[{ required: true }]}
          >
            <Input placeholder="A name for the team" />
          </Form.Item>
          <Form.Item
            name="description" label="Description"
          >
            <Input.TextArea placeholder="A description for the team" />
          </Form.Item>
          <Form.Item>
            <Button type='primary' htmlType='submit' loading={createTeamLoading}>Create Team</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>

  )
}
